import { useMemo, useState } from "react";
import { Copy, KeyRound, Loader2, RefreshCw, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ComboboxSelect } from "@/components/ui/combobox-select";

interface ClientApiCredentialsSectionProps {
  companyId: number;
  channelId: number | null | undefined;
  readOnly?: boolean;
}

function formatDateTime(value: string | Date | null | undefined) {
  if (!value) return "Nunca";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "Nunca";
  return date.toLocaleString("pt-BR");
}

function ApiCredentialsAuditSection({ companyId, readOnly }: { companyId: number; readOnly: boolean }) {
  const [cursorHistory, setCursorHistory] = useState<number[]>([]);
  const currentCursor = cursorHistory[cursorHistory.length - 1];

  const auditQuery = trpc.apiCredentials.listAudit.useQuery(
    { companyId, limit: 20, beforeId: currentCursor },
    { enabled: !readOnly, refetchOnWindowFocus: false },
  );

  const handleCopyJobId = (jobPublicId: string) => {
    navigator.clipboard.writeText(jobPublicId);
    toast.success("UUID do lote copiado.");
  };

  if (readOnly) return null;

  const items = auditQuery.data?.items ?? [];

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle>Auditoria de utilização da API</CardTitle>
        <CardDescription>Cada chamada autenticada com uma credencial deste cliente aparece aqui.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {auditQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Carregando auditoria...
          </div>
        ) : items.length === 0 ? (
          <Alert>
            <AlertDescription>Nenhuma chamada registrada para as credenciais deste cliente ainda.</AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-4">Data</th>
                    <th className="py-2 pr-4">Usuário</th>
                    <th className="py-2 pr-4">Endpoint</th>
                    <th className="py-2 pr-4">Arquivo</th>
                    <th className="py-2 pr-4">Lote</th>
                    <th className="py-2 pr-4">Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((entry: any) => (
                    <tr key={entry.id} className="border-b border-slate-100">
                      <td className="py-3 pr-4 text-slate-600">{formatDateTime(entry.createdAt)}</td>
                      <td className="py-3 pr-4 text-slate-600">{entry.userName || `Usuário #${entry.userId}`}</td>
                      <td className="py-3 pr-4 font-mono text-xs text-slate-600">
                        {entry.method} {entry.endpoint}
                      </td>
                      <td className="py-3 pr-4 text-slate-600">{entry.fileName || "—"}</td>
                      <td className="py-3 pr-4">
                        {entry.jobPublicId ? (
                          <button
                            type="button"
                            className="font-mono text-xs text-slate-600 underline decoration-dotted hover:text-slate-900"
                            onClick={() => handleCopyJobId(entry.jobPublicId)}
                            title="Copiar UUID do lote"
                          >
                            {entry.jobPublicId.slice(0, 8)}…
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant={entry.httpStatus < 400 ? "secondary" : "outline"}>{entry.httpStatus}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between">
              <Button
                size="sm"
                variant="outline"
                disabled={cursorHistory.length === 0}
                onClick={() => setCursorHistory((current) => current.slice(0, -1))}
              >
                Página anterior
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!auditQuery.data?.nextCursor}
                onClick={() => auditQuery.data?.nextCursor && setCursorHistory((current) => [...current, auditQuery.data!.nextCursor as number])}
              >
                Próxima página
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function ClientApiCredentialsSection({
  companyId,
  channelId,
  readOnly = false,
}: ClientApiCredentialsSectionProps) {
  const utils = trpc.useUtils();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formUserId, setFormUserId] = useState("");
  const [credentialName, setCredentialName] = useState("");
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const [revokeTargetId, setRevokeTargetId] = useState<number | null>(null);
  const [rotateTargetId, setRotateTargetId] = useState<number | null>(null);

  const credentialsQuery = trpc.apiCredentials.listByCompany.useQuery(
    { companyId },
    { enabled: !readOnly && Boolean(companyId), refetchOnWindowFocus: false },
  );

  const usersQuery = trpc.users.listByCompany.useQuery(
    { companyId },
    { enabled: createDialogOpen, refetchOnWindowFocus: false },
  );

  const createMutation = trpc.apiCredentials.create.useMutation({
    onSuccess: (result) => {
      setGeneratedToken(result.token);
      setCreateDialogOpen(false);
      setFormUserId("");
      setCredentialName("");
      utils.apiCredentials.listByCompany.invalidate({ companyId });
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao criar a credencial.");
    },
  });

  const revokeMutation = trpc.apiCredentials.revoke.useMutation({
    onSuccess: (result) => {
      toast.success(result.message);
      setRevokeTargetId(null);
      utils.apiCredentials.listByCompany.invalidate({ companyId });
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao revogar a credencial.");
      setRevokeTargetId(null);
    },
  });

  const rotateMutation = trpc.apiCredentials.rotate.useMutation({
    onSuccess: (result) => {
      setGeneratedToken(result.token);
      setRotateTargetId(null);
      utils.apiCredentials.listByCompany.invalidate({ companyId });
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao substituir a credencial.");
      setRotateTargetId(null);
    },
  });

  const userOptions = useMemo(
    () =>
      (usersQuery.data ?? []).map((user: any) => ({
        value: user.id.toString(),
        label: user.email ? `${user.name} (${user.email})` : user.name,
      })),
    [usersQuery.data],
  );

  const credentials = credentialsQuery.data?.credentials ?? [];

  const handleCreate = () => {
    if (!channelId) {
      toast.error("Este cliente não possui um canal vinculado. Ajuste o cadastro antes de criar uma credencial.");
      return;
    }
    if (!formUserId || credentialName.trim().length < 3) {
      toast.error("Selecione o usuário técnico e informe um nome com pelo menos 3 caracteres.");
      return;
    }
    createMutation.mutate({
      companyId,
      channelId,
      userId: Number(formUserId),
      credentialName: credentialName.trim(),
    });
  };

  return (
    <div className="space-y-6">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-1">
              <CardTitle>Credenciais da API — Carga de Vidas</CardTitle>
              <CardDescription>
                Permite que sistemas externos deste cliente enviem atualizações da base de vidas via API, sem acesso ao
                Portal.
              </CardDescription>
            </div>
            {!readOnly && (
              <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
                <KeyRound className="mr-2 h-4 w-4" />
                Nova credencial
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {readOnly ? (
            <Alert>
              <AlertDescription>Somente administradores podem gerenciar credenciais de API.</AlertDescription>
            </Alert>
          ) : credentialsQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando credenciais...
            </div>
          ) : credentials.length === 0 ? (
            <Alert>
              <AlertDescription>Nenhuma credencial de API cadastrada para este cliente ainda.</AlertDescription>
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-4">Label</th>
                    <th className="py-2 pr-4">Usuário</th>
                    <th className="py-2 pr-4">E-mail</th>
                    <th className="py-2 pr-4">Prefixo</th>
                    <th className="py-2 pr-4">Criado em</th>
                    <th className="py-2 pr-4">Último uso</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {credentials.map((credential: any) => (
                    <tr key={credential.id} className="border-b border-slate-100">
                      <td className="py-3 pr-4 font-medium text-slate-950">{credential.credentialName}</td>
                      <td className="py-3 pr-4 text-slate-600">{credential.userName || `Usuário #${credential.userId}`}</td>
                      <td className="py-3 pr-4 text-slate-600">{credential.userEmail || "—"}</td>
                      <td className="py-3 pr-4 font-mono text-xs text-slate-600">{credential.tokenPrefix}…</td>
                      <td className="py-3 pr-4 text-slate-600">{formatDateTime(credential.createdAt)}</td>
                      <td className="py-3 pr-4 text-slate-600">{formatDateTime(credential.lastUsedAt)}</td>
                      <td className="py-3 pr-4">
                        <Badge variant={credential.isActive ? "secondary" : "outline"}>
                          {credential.isActive ? "Ativa" : "Revogada"}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4">
                        {credential.isActive && (
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => setRotateTargetId(credential.id)}>
                              <RefreshCw className="mr-2 h-3.5 w-3.5" />
                              Substituir
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setRevokeTargetId(credential.id)}>
                              <ShieldOff className="mr-2 h-3.5 w-3.5" />
                              Revogar
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nova credencial de API</DialogTitle>
              <DialogDescription>
                A credencial concede acesso apenas à base de vidas deste cliente, através do usuário técnico informado.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Usuário técnico vinculado</Label>
                <ComboboxSelect
                  options={userOptions}
                  value={formUserId}
                  onValueChange={setFormUserId}
                  placeholder="Selecione o usuário"
                  searchPlaceholder="Busque pelo nome do usuário..."
                  emptyText="Nenhum usuário encontrado para este cliente."
                />
                <p className="text-xs text-slate-500">
                  As importações feitas por esta credencial ficam registradas em nome deste usuário.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Nome da credencial</Label>
                <Input
                  value={credentialName}
                  onChange={(event) => setCredentialName(event.target.value)}
                  placeholder="Ex.: Integração ERP folha de pagamento"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreate} disabled={createMutation.isPending}>
                {createMutation.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Criando...</> : "Criar credencial"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={Boolean(generatedToken)} onOpenChange={(open) => !open && setGeneratedToken(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Credencial gerada</DialogTitle>
              <DialogDescription>Copie o token agora — ele não poderá ser recuperado depois.</DialogDescription>
            </DialogHeader>
            {generatedToken && (
              <div className="space-y-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
                  Esta chave será exibida apenas uma vez. Guarde-a em local seguro.
                </p>
                <div className="flex items-center justify-between gap-3 rounded-xl bg-white p-3">
                  <code className="break-all font-mono text-sm text-emerald-950">{generatedToken}</code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(generatedToken);
                      toast.success("Token copiado para a área de transferência.");
                    }}
                  >
                    <Copy className="mr-2 h-3.5 w-3.5" />
                    Copiar
                  </Button>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setGeneratedToken(null)}>Concluído</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={revokeTargetId !== null} onOpenChange={(open) => !open && setRevokeTargetId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Revogar esta credencial?</AlertDialogTitle>
              <AlertDialogDescription>
                A chave será invalidada imediatamente e não poderá ser recuperada. Qualquer integração que a utilize
                passará a receber erro de autenticação.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => revokeTargetId && revokeMutation.mutate({ credentialId: revokeTargetId })}
                disabled={revokeMutation.isPending}
              >
                {revokeMutation.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Revogando...</> : "Revogar"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={rotateTargetId !== null} onOpenChange={(open) => !open && setRotateTargetId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Substituir esta credencial?</AlertDialogTitle>
              <AlertDialogDescription>
                A credencial atual será revogada e uma nova será criada no mesmo escopo. O token atual deixa de funcionar
                imediatamente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => rotateTargetId && rotateMutation.mutate({ credentialId: rotateTargetId })}
                disabled={rotateMutation.isPending}
              >
                {rotateMutation.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Substituindo...</> : "Substituir"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>

      <ApiCredentialsAuditSection companyId={companyId} readOnly={readOnly} />
    </div>
  );
}
